/**
 * Onboarding Context Provider
 * Epic 13: Central de Ajuda e Suporte
 * Story 13.4: Onboarding Guiado - Global State Management
 *
 * Provides global onboarding state and tour control functions
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import Shepherd, { type Tour, type StepOptionsWhen } from "shepherd.js";
import "shepherd.js/dist/css/shepherd.css";
import { useDebouncedCallback } from "use-debounce";
import {
  useShouldShowTour,
  useTourDefinition,
  useProgress,
  useUpdateProgress,
} from "@/hooks/useOnboarding";
import { TourDefinition, TourStep } from "@/services/onboardingApi";
import { enhanceStepTextWithManualLink } from "@/lib/onboardingUtils";
import { useToast } from "@/hooks/use-toast";

// ============================================================================
// Types
// ============================================================================

interface OnboardingContextType {
  // Tour state
  tourInstance: Tour | null;
  isOnboardingModalOpen: boolean;
  shouldShowOnboarding: boolean;
  recommendedTourId: string | null;

  // Tour control
  startTour: (tourId: string) => void;
  stopTour: () => void;
  skipTour: () => void;
  resetTour: () => void;

  // Modal control
  openOnboardingModal: () => void;
  closeOnboardingModal: () => void;

  // Progress
  currentTourId: string | null;
  currentProgress: number;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

// ============================================================================
// Provider Component
// ============================================================================

interface OnboardingProviderProps {
  children: ReactNode;
  userId?: string;
  userRole?: string;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
  userId,
  userRole,
}) => {
  const [tourInstance, setTourInstance] = useState<Tour | null>(null);
  const [isOnboardingModalOpen, setIsOnboardingModalOpen] = useState(false);
  const [currentTourId, setCurrentTourId] = useState<string | null>(null);
  const [currentProgress, setCurrentProgress] = useState(0);
  const { toast } = useToast();

  // Check if should show onboarding (AC1)
  const { data: shouldShowData, isSuccess: shouldShowLoaded } = useShouldShowTour();

  // Get tour definition when currentTourId changes
  const { data: tourDefinition } = useTourDefinition(currentTourId || "");

  // Get progress for current tour
  const { data: progressData } = useProgress(currentTourId || "");
  const updateProgressMutation = useUpdateProgress(currentTourId || "");

  // AC1: Auto-show onboarding modal on first login
  useEffect(() => {
    if (shouldShowLoaded && shouldShowData?.should_show) {
      setIsOnboardingModalOpen(true);
      setCurrentTourId(shouldShowData.tour_id);
    }
  }, [shouldShowData, shouldShowLoaded]);

  // Update progress when progressData changes
  useEffect(() => {
    if (progressData) {
      setCurrentProgress(progressData.completion_percentage);
    }
  }, [progressData]);

  // ========================================================================
  // Tour Control Functions
  // ========================================================================

  // Code Review Fix #8: Debounced progress update (2s delay)
  // Prevents excessive API calls during rapid step navigation
  const debouncedUpdateProgress = useDebouncedCallback(
    (data: any) => {
      updateProgressMutation.mutate(data);
    },
    2000, // 2 second delay as per Story 13.4 Subtask 9.3
    { leading: false, trailing: true }
  );

  // Code Review Fix #10: DOM validation before tour start
  // Prevents Shepherd.js crashes when target elements don't exist
  const validateTourSteps = useCallback(
    (steps: TourStep[]): { isValid: boolean; missingElements: string[] } => {
      const missingElements: string[] = [];

      steps.forEach((step) => {
        if (step.attachTo?.element) {
          const element = document.querySelector(step.attachTo.element);
          if (!element) {
            missingElements.push(`Step "${step.id}": Element "${step.attachTo.element}" not found`);
          }
        }
      });

      return {
        isValid: missingElements.length === 0,
        missingElements,
      };
    },
    []
  );

  const createShepherdTour = useCallback(
    (definition: TourDefinition): Tour => {
      const tour = new Shepherd.Tour({
        useModalOverlay: true,
        defaultStepOptions: {
          classes: "shepherd-theme-custom",
          scrollTo: true,
          cancelIcon: {
            enabled: true,
          },
        },
      });

      // Add steps
      definition.steps.forEach((step: TourStep, index: number) => {
        // Task 17: Enhance step text with manual deep link if available
        // Note: manual_section_id should be the section slug for URL generation
        // TODO: Update backend to provide slug or create ID->slug mapping
        const enhancedText = step.manual_section_id
          ? enhanceStepTextWithManualLink(step.text, step.manual_section_id)
          : step.text;

        tour.addStep({
          id: step.id,
          title: step.title,
          text: enhancedText,
          attachTo: step.attachTo
            ? {
                element: step.attachTo.element,
                on: step.attachTo.on,
              }
            : undefined,
          classes: step.classes || "",
          scrollTo: step.scrollTo !== false,
          buttons: step.buttons.map((btn) => ({
            text: btn.text,
            action() {
              if (btn.action === "next") {
                // Code Review Fix #8: Use debounced update for step navigation
                // Reduces API calls from 7 rapid requests to 1 batched request per 2s
                debouncedUpdateProgress({
                  current_step: index + 1,
                  completed_steps: [...(progressData?.completed_steps || []), step.id],
                });
                tour.next();
              } else if (btn.action === "back") {
                tour.back();
              } else if (btn.action === "complete") {
                // Critical action: immediate update (no debounce)
                updateProgressMutation.mutate({
                  status: "completed",
                  current_step: definition.steps.length - 1,
                  completed_steps: definition.steps.map((s) => s.id),
                });
                tour.complete();
              } else if (btn.action === "skip") {
                // Critical action: immediate update (no debounce)
                updateProgressMutation.mutate({
                  status: "skipped",
                });
                tour.cancel();
              }
            },
            classes: btn.classes || "shepherd-button-primary",
          })),
          // API entrega `when` como Record genérico (JSON não carrega funções);
          // Shepherd espera StepOptionsWhen. Cast de interop — runtime inalterado.
          when: step.when as StepOptionsWhen,
        });
      });

      // Event listeners
      tour.on("complete", () => {
        setTourInstance(null);
        setCurrentTourId(null);
      });

      tour.on("cancel", () => {
        setTourInstance(null);
        setCurrentTourId(null);
      });

      return tour;
    },
    [progressData, updateProgressMutation, debouncedUpdateProgress]
  );

  const startTour = useCallback(
    (tourId: string) => {
      // Stop existing tour
      if (tourInstance) {
        tourInstance.complete();
      }

      setCurrentTourId(tourId);
      setIsOnboardingModalOpen(false);

      // Create new tour (will trigger via useEffect when tourDefinition loads)
    },
    [tourInstance]
  );

  // Effect to start tour when definition is loaded
  useEffect(() => {
    if (tourDefinition && currentTourId && !tourInstance) {
      // Code Review Fix #10: Validate DOM elements before creating tour
      // Code Review Session 2 Fix: Added user-facing toast notification ✅
      const validation = validateTourSteps(tourDefinition.steps);

      if (!validation.isValid) {
        // Silently skip validation warnings - tour elements may not be available on all pages
        // console.warn('⚠️ Onboarding tour validation failed - some elements are missing:', validation.missingElements);

        // Filter out steps with missing elements (graceful degradation)
        const validSteps = tourDefinition.steps.filter((step) => {
          if (!step.attachTo?.element) return true; // Include steps without attachTo
          return document.querySelector(step.attachTo.element) !== null;
        });

        if (validSteps.length === 0) {
          // Silently cancel tour if no elements are available - this is expected on non-dashboard pages
          // console.error('❌ Cannot start tour - all steps have missing DOM elements');
          setCurrentTourId(null); // Reset tour state
          return;
        }

        // Only show notification if some (but not all) steps are missing
        if (validation.missingElements.length > 0 && validSteps.length > 0) {
          toast({
            title: "Tour parcialmente disponível",
            description: `${validation.missingElements.length} passos foram omitidos pois os elementos não estão disponíveis nesta página.`,
            variant: "default",
          });
        }

        // Create tour with only valid steps
        const filteredDefinition = { ...tourDefinition, steps: validSteps };
        const newTour = createShepherdTour(filteredDefinition);
        setTourInstance(newTour);

        setTimeout(() => {
          newTour.start();
          updateProgressMutation.mutate({
            status: "in_progress",
            current_step: 0,
          });
        }, 300);
      } else {
        // All elements valid - proceed normally
        const newTour = createShepherdTour(tourDefinition);
        setTourInstance(newTour);

        setTimeout(() => {
          newTour.start();
          updateProgressMutation.mutate({
            status: "in_progress",
            current_step: 0,
          });
        }, 300); // Small delay to ensure DOM is ready
      }
    }
  }, [
    tourDefinition,
    currentTourId,
    tourInstance,
    createShepherdTour,
    updateProgressMutation,
    validateTourSteps,
  ]);

  const stopTour = useCallback(() => {
    if (tourInstance) {
      tourInstance.complete();
      setTourInstance(null);
      setCurrentTourId(null);
    }
  }, [tourInstance]);

  const skipTour = useCallback(() => {
    // Persist skip to backend regardless of tour instance state
    // (user may skip from the modal before any Shepherd tour is created)
    updateProgressMutation.mutate({
      status: "skipped",
    });
    if (tourInstance) {
      tourInstance.cancel();
      setTourInstance(null);
    }
    setCurrentTourId(null);
    setIsOnboardingModalOpen(false);
  }, [tourInstance, updateProgressMutation]);

  const resetTour = useCallback(() => {
    if (currentTourId) {
      updateProgressMutation.mutate({
        status: "not_started",
        current_step: null,
        completed_steps: [],
      });
      stopTour();
    }
  }, [currentTourId, updateProgressMutation, stopTour]);

  // ========================================================================
  // Modal Control Functions
  // ========================================================================

  const openOnboardingModal = useCallback(() => {
    setIsOnboardingModalOpen(true);
  }, []);

  const closeOnboardingModal = useCallback(() => {
    setIsOnboardingModalOpen(false);
  }, []);

  // ========================================================================
  // Context Value
  // ========================================================================

  const value: OnboardingContextType = {
    tourInstance,
    isOnboardingModalOpen,
    shouldShowOnboarding: shouldShowData?.should_show || false,
    recommendedTourId: shouldShowData?.tour_id || null,
    startTour,
    stopTour,
    skipTour,
    resetTour,
    openOnboardingModal,
    closeOnboardingModal,
    currentTourId,
    currentProgress,
  };

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

// ============================================================================
// Hook
// ============================================================================

export const useOnboardingContext = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboardingContext must be used within OnboardingProvider");
  }
  return context;
};

export default OnboardingContext;
